class UnderFlowError extends Error {}

export interface SearchEntry {
  token: string;
  doc: number;
  score: number;
}

/**
 * Simple decoder for an index that attempts to minimise number of bytes transferred.
 */
export class IndexDecoder {
  r: Uint8Array | undefined = undefined;
  pos: number = 0;
  lastToken: string | undefined = undefined;
  docId: number = 0;
  txtDecoder = new TextDecoder();

  /**
   * Adds a chunk to any bytes that remain in the buffer.  This allows us to leave some bytes unread
   * when parsing a chunk, and then to append the new chunk when it arrives and continue parsing.
   * @param chunk
   */
  append(chunk: Uint8Array) {
    if (!this.r) {
      // First call to append, we just use the chunk.
      this.r = chunk;
    } else {
      let remaining = this.r.length - this.pos;
      if (remaining == 0) {
        // The old chunk was completely read. Just use the one passed in.
        this.r = chunk;
      } else {
        // console.log("appending", chunk, " to", this.r.subarray(this.pos, this.pos + remaining));
        // Make a new array big enough for the new chunk and the remainer from the original, then copy in data.
        const result = new Uint8Array(remaining + chunk.length);
        result.set(this.r.subarray(this.pos, this.pos + remaining), 0);
        result.set(chunk, remaining);
        this.r = result;
      }
    }
    // Reset the position to the front of the buffer.
    this.pos = 0;
  }

  parse(chunk: Uint8Array): SearchEntry[] {
    const res: SearchEntry[] = [];
    this.append(chunk);
    // console.log("Parsing", this.r);

    // The mark stores the position that we know we have consumed up to.  This allows us to
    // revert back when we get an underflow.
    let mark = this.pos;
    try {
      while (true) {
        if (!this.lastToken) {
          const tok = this.readString();
          // console.log("Read token", tok);
          if (!tok) {
            break;
          }
          this.lastToken = tok;
          this.docId = 0;
        }

        mark = this.pos;
        const docIdDelta = this.readUnsignedInt();
        if (docIdDelta == null) {
          break;
        }
        // console.log("Delta", docIdDelta);
        if (docIdDelta == 0) {
          this.lastToken = undefined;
        } else {
          // Don't update this.docId yet, because we might break in the middle
          const newDocId = this.docId + docIdDelta;
          const score = this.readUnsignedInt();
          // console.log("Score", score);
          if (score == null) {
            break;
          }
          // console.log("Pushing", this.lastToken, newDocId, score);
          res.push({ token: this.lastToken, doc: newDocId, score });
          this.docId = newDocId;
        }
        mark = this.pos;
      }
    } catch (ex) {
      if (!(ex instanceof UnderFlowError)) {
        throw ex;
      }
    }
    // Revert our pointer back to the start of record, so that when we add a new chunk
    // we can read the whole record in one go.
    this.pos = mark;

    // console.log("Returning", res);
    return res;
  }

  private readByte() {
    if (this.remaining() < 1) {
      return null;
    }
    return this.r![this.pos++];
  }

  remaining() {
    return this.r ? this.r.length - this.pos : 0;
  }

  private readUnsignedInt() {
    let val = 0;
    let firstByte = true;
    let b: number | null;
    do {
      b = this.readByte();
      if (b === null) {
        throw new UnderFlowError();
      }
      val = (val << 7) | (b & 0x7f);
      firstByte = false;
    } while (b & 0x80);
    return val;
  }

  private readString() {
    const sz = this.readUnsignedInt();
    if (sz === null) {
      return null;
    }
    if (this.remaining() < sz) {
      throw new UnderFlowError();
    }
    const sval = this.txtDecoder.decode(this.r!.subarray(this.pos, this.pos + sz));
    this.pos += sz;
    return sval;
  }
}
